/*
 * Bodenberater — REUD Bodenarena
 * assets/bodenberater.js
 *
 * Q1 Raum · Q2 Nutzung · Q3 Eigenschaften 
 * · Q4 Optik · Q5 Verlegeart
 *
 * Ranking model — Optik & Color FIRST, technical signals SECOND.
 *
 *   PRIMARY (lexicographic):  Optik+Color match tier
 *     2 — full match:    chosen colour matches (color-OR) AND every chosen
 *                        pattern matches (pattern-AND)
 *     1 — partial:       matches at least one chosen Optik key but not all
 *     0 — no match       (or: user picked "Keine Präferenz")
 *
 *   SECONDARY (within tier):  technical score from scoreProduct()
 *     Hard physical rules (Bad/Küche/feuchtraum, NK floor for Flur/Gewerbe)
 *     are applied first as exclusions. Remaining tech preferences
 *     (Eigenschaften, Verlegeart, Nutzung, Feuchtigkeit, Raum) accumulate
 *     into the score that orders the cohort.
 *
 * Result: products that match the user's Optik intent ALWAYS rank above
 * products that don't, regardless of technical score gap. Within each
 * Optik tier, the most technically-fitting products surface first.
 */

function BodenberaterInit() {
  'use strict';

  if (window.__bodenberaterInited) return;
  window.__bodenberaterInited = true;

  var MIN_R       = 3;
  var MAX_R       = 6;
  var OPTIK_SLOTS = 3;

  /* ─── PRODUCT HELPERS ─── */

  function isVinyl(p)       { return p.art.indexOf('vinyl') >= 0; }
  function isDesignboden(p) { return p.art.indexOf('designboden') >= 0; }
  function isParkett(p)     { return p.art.indexOf('parkett') >= 0; }
  function isLaminat(p)     { return p.art.indexOf('laminat') >= 0; }

  function isFeuchtraum(p) {
    return p.sonstiges.indexOf('feuchtraum')     >= 0
        || p.sonstiges.indexOf('aquastop')        >= 0
        || p.sonstiges.indexOf('wasserresistent') >= 0;
  }

  function isFBH(p) {
    return p.sonstiges.indexOf('bodenheizung') >= 0;
  }

  function hasTrittschall(p) {
    return p.trittschall.indexOf('integriert') >= 0;
  }

  function isFliese(p) {
    return p.plankenformat === 'fliese'
        || p.optik.indexOf('fliesenoptik') >= 0;
  }

  function isFischgraet(p) {
    return p.optik.indexOf('fischgr')                       >= 0
        || p.title.toLowerCase().indexOf('fischgr')         >= 0
        || p.title.toLowerCase().indexOf('herringbone')     >= 0;
  }

  function isHolz(p) {
    return p.optik.indexOf('holz')          >= 0   /* "Holzoptik", "Naturholz"      */
        || p.optik.indexOf('eiche')         >= 0   /* "Eichenoptik" (Eiche = Holz)  */
        || p.optik.indexOf('diele')         >= 0   /* "Landhausdielenoptik"         */
        || p.plankenformat.indexOf('diele') >= 0   /* Plankenformat "Landhausdiele" */
        || isParkett(p);                            /* Echtholz-Parkett              */
  }

  function isKlick(p)  { return p.verlegeart.indexOf('klick') >= 0; }
  function isKleben(p) { return p.verlegeart.indexOf('kleb')  >= 0; }

  function matchesFarbe(p, key) {
    return !!p.farbe && p.farbe.indexOf(key) >= 0;
  }

  function parseNS(p) {
    if (typeof p._nsNum === 'number') return p._nsNum;
    var s = String(p.nutzungsschicht || '').replace(',', '.').replace(/[^0-9.]/g, '');
    return p._nsNum = parseFloat(s) || 0;
  }

  function parseStaerke(p) {
    if (typeof p._starkeNum === 'number') return p._starkeNum;
    var s = String(p.starke || '').replace(',', '.').replace(/[^0-9.]/g, '');
    return p._starkeNum = parseFloat(s) || 0;
  }

  function nsTier(p) {
    if (typeof p._nsTier === 'number') return p._nsTier;
    var ns = parseNS(p);
    var tier;
    if (isParkett(p)) {
      if      (ns >= 4)   tier = 4;
      else if (ns >= 3)   tier = 3;
      else if (ns >= 2.5) tier = 2;
      else if (ns >= 1.5) tier = 1;
      else                tier = 0;
    } else {
      if      (ns >= 0.55) tier = 4;
      else if (ns >= 0.50) tier = 3;
      else if (ns >= 0.40) tier = 2;
      else if (ns >= 0.30) tier = 1;
      else                 tier = 0;
    }
    return p._nsTier = tier;
  }

  function matchesOptikKey(p, key) {
    if (key === 'fischgraet') return isFischgraet(p);
    if (key === 'fliese')     return isFliese(p);
    if (key === 'holz')       return isHolz(p);
    if (key === 'hell' || key === 'mittel' || key === 'dunkel') return matchesFarbe(p, key);
    return false;
  }

  function matchesAnyOptik(p, keys) {
    return keys.some(function (k) { return matchesOptikKey(p, k); });
  }

  /* ── QUESTIONS ── */
  var Qs = [

    /* Q1 – RAUM */
    {
      id: 'raum', type: 'img', multi: false, maxSel: 1,
      label: 'Raum', step: '1 / 5',
      q: 'In welchem Raum wird der Boden verlegt?',
      sub: 'Der Raum legt die grundlegenden technischen Anforderungen fest.',
      imgSrc: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&q=80',
      imgLabel: 'Welcher Raum?',
      opts: [
        {
          key: 'trocken_group',
          l: 'Wohnraum',
          s: 'Wohnzimmer · Schlafzimmer · Kinderzimmer · Flur',
          rooms: ['Wohnzimmer', 'Schlafzimmer', 'Kinderzimmer', 'Flur'],
          img: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&q=80',
        },
        {
          key: 'feucht_group',
          l: 'Feuchtraum',
          s: 'Küche · Bad',
          rooms: ['Küche', 'Bad'],
          img: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&q=80',
          tip: 'In Feuchträumen kommen ausschließlich feuchtraumgeeignete Böden in Frage — Vinyl ist die optimale Wahl.',
        },
        {
          key: 'verkehr_group',
          l: 'Gewerbe',
          s: 'Eingang · Büro / Gewerbe',
          rooms: ['Eingang', 'Büro / Gewerbe'],
          img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
        },
      ]
    },

    /* Q2 – NUTZUNG */
    {
      id: 'nutzung', type: 'icon', multi: false, maxSel: 1,
      label: 'Nutzung', step: '2 / 5',
      q: 'Wie wird der Boden täglich genutzt?',
      sub: 'Davon hängt ab, wie robust der Boden sein muss.',
      imgSrc: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80',
      imgLabel: 'Beanspruchung',
      opts: [
        { key: 'normal',    ic: '🏠', l: 'Normaler Haushalt',  s: 'Erwachsene, keine besonderen Anforderungen' },
        { key: 'kinder',    ic: '👶', l: 'Kinder',             s: 'Lebhaftes Spielen, Stöße, Lärm' },
        { key: 'haustiere', ic: '🐾', l: 'Haustiere',          s: 'Krallen, Schmutz, intensiver Abrieb' },
        { key: 'stark',     ic: '🏢', l: 'Hohe Beanspruchung', s: 'Viele Personen, gewerbliche Nutzung' },
      ]
    },

    /* Q3 – EIGENSCHAFTEN */
    {
      id: 'eigenschaften', type: 'icon', multi: true, maxSel: 2,
      label: 'Eigenschaften', step: '3 / 5',
      q: 'Welche Eigenschaften sind dir wichtig?',
      sub: 'Wähle bis zu 2 Eigenschaften, die für dich entscheidend sind.',
      imgSrc: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
      imgLabel: 'Anforderungen',
      opts: [
        { key: 'fbh_eig',     ic: '🔥', l: 'Fußbodenheizung',       s: 'Kompatibel mit Warmwasser- oder Elektro-FBH' },
        { key: 'trittschall', ic: '🔇', l: 'Trittschalldämmung',    s: 'Ruhigere Geräuschentwicklung — wichtig in Apartments' },
        { key: 'kratzfest',   ic: '💪', l: 'Kratzfest',             s: 'Hohe Nutzungsschicht, robust gegen Kratzer' },
        { key: 'pflegeleicht',ic: '🧹', l: 'Pflegeleicht',           s: 'Einfache Reinigung, wasserabweisende Oberfläche' },
        { key: 'natuerlich',  ic: '🌿', l: 'Natürliche Materialien', s: 'Echtholz, Parkett oder naturnahe Böden' },
      ]
    },

    /* Q4 – OPTIK */
    {
      id: 'optik', type: 'icon', multi: true, maxSel: 2,
      label: 'Optik', step: '4 / 5',
      q: 'Welche Optik spricht dich an?',
      sub: 'Wähle bis zu 2 Optionen — oder „Keine Präferenz" für rein technische Empfehlungen.',
      imgSrc: 'https://images.unsplash.com/photo-1615873968403-89e068629265?w=800&q=80',
      imgLabel: 'Stil & Farbe',
      opts: [
        { key: 'hell',       ic: '🌾', l: 'Hell',            s: 'Helle Eiche, Ahorn, Beige, Nordic' },
        { key: 'mittel',     ic: '🟫', l: 'Mittel',          s: 'Natürliche Eiche, Honig, Karamell' },
        { key: 'dunkel',     ic: '🌑', l: 'Dunkel',          s: 'Nuss, Wenge, Dunkelbraun' },
        { key: 'holz',       ic: '🪵', l: 'Holzoptik',       s: 'Natürliche Holzmaserung & Dielenoptik' },
        { key: 'fischgraet', ic: '▲',  l: 'Fischgrät',       s: 'Klassisches Muster, zeitlos elegant' },
        { key: 'fliese',     ic: '⬜', l: 'Fliesenoptik',    s: 'Stein-, Marmor- oder Betonoptik' },
        { key: 'egal',       ic: '✦',  l: 'Keine Präferenz', s: 'Funktion ist mir wichtiger als Optik' },
      ]
    },

    /* Q5 – VERLEGEART */
    {
      id: 'verlegeart', type: 'list', multi: false, maxSel: 1,
      label: 'Verlegung', step: '5 / 5',
      q: 'Wie soll der Boden verlegt werden?',
      sub: 'Klick ist ideal zum Selbstverlegen. Kleben eignet sich bei niedrigen Aufbauhöhen und Renovierungen.',
      imgSrc: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
      imgLabel: 'Verlegeart',
      opts: [
        { key: 'klick',     ic: '🔗',  l: 'Klick-System',    s: 'Schwimmend verlegt, DIY-freundlich, flexibel & rückbaubar' },
        { key: 'kleben',    ic: '🖊️',  l: 'Verklebt',        s: 'Fest mit Untergrund verbunden, dünn, optimal für Renovierung' },
        { key: 'egal_verl', ic: '↕',   l: 'Keine Präferenz', s: 'Beide Varianten sind für mich in Ordnung' },
      ]
    },
  ];

  /* ─── DOM + STATE ─── */
  var root = document.getElementById('br-quiz-root');
  if (!root) return;

  var ans      = {};
  var products = [];

  /* ─── PUBLIC API ─── */
  var overlay = document.getElementById('br-modal-overlay');

  window.brOpenModal = function () {
    if (!overlay) return;
    overlay.classList.add('is-open');
    document.body.classList.add('br-modal-open');
    if (window.gtag) gtag('event', 'bodenberater_opened');
  };
  window.brCloseModal = function () {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.classList.remove('br-modal-open');
  };

  if (overlay) {
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) window.brCloseModal();
    });
  }

  /* ── BOOT ── */
  loadProducts();
  renderEntry();

  /* ── PRODUCT LOADING ── */
  function loadProducts() {
    var el = document.getElementById('br-product-data');
    if (!el) return;
    try {
      var raw = JSON.parse(el.textContent || el.innerHTML || '[]');
      products = raw.map(function (p) {
        return {
          id:              String(p.id            || ''),
          title:           String(p.title         || ''),
          url:             String(p.url           || '#'),
          img:             String(p.img           || ''),
          art:             flat(p.art).toLowerCase(),
          sonstiges:       flat(p.sonstiges).toLowerCase(),
          verlegeart:      flat(p.verlegeart).toLowerCase(),
          optik:           flat(p.optik).toLowerCase(),
          farbe:           flat(p.farbe).toLowerCase().trim(),
          nk:              parseInt(flat(p.nutzungsklasse), 10) || 0,
          nutzungsschicht: flat(p.nutzungsschicht),
          trittschall:     flat(p.trittschall).toLowerCase(),
          plankenformat:   flat(p.plankenformat).toLowerCase(),
          starke:          flat(p.starke),
        };
      });
      console.log('[Bodenberater] Products loaded:', products.length);
    } catch (e) {
      console.warn('[Bodenberater] Produktdaten konnten nicht geladen werden', e);
    }
  }

  function flat(v) {
    if (!v) return '';
    if (Array.isArray(v)) return v.join(' ');
    return String(v);
  }

  /* ── ENTRY SCREEN ── */
  function renderEntry() {
    root.innerHTML =
      '<div class="br-entry br-fade-in">' +
        '<div class="br-entry-left">' +
          '<div class="br-entry-left-inner">' +
            '<span class="br-eyebrow">Bodenberater</span>' +
            '<h1 class="br-entry-title">Den richtigen Boden finden —<br>in 60 Sekunden.</h1>' +
            '<p class="br-entry-sub">Beantworte 5 kurze Fragen und erhalte eine persönliche Empfehlung, die wirklich zu deinem Raum passt.</p>' +
            '<div class="br-entry-steps">' +
              entryPill('Raum') + entryPill('Nutzung') +
              entryPill('Eigenschaften') + entryPill('Optik') + entryPill('Verlegung') +
            '</div>' +
            '<button class="br-start-btn" id="br-start-btn">Beratung starten <span class="br-start-arrow">→</span></button>' +
            '<p class="br-entry-note">Kostenlos &nbsp;·&nbsp; Keine Anmeldung &nbsp;·&nbsp; 60 Sekunden</p>' +
          '</div>' +
        '</div>' +
        '<div class="br-entry-right">' +
          '<img src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=900&q=85" alt="Hochwertiger Boden" loading="lazy">' +
          '<div class="br-entry-right-overlay"></div>' +
        '</div>' +
      '</div>';

    document.getElementById('br-start-btn').addEventListener('click', function () {
      ans = {}; renderQuestion(0);
    });
  }

  function entryPill(label) {
    return '<span class="br-entry-pill">' + esc(label) + '</span>';
  }

  /* ── RENDER QUESTION ── */
  function renderQuestion(idx) {
    var q      = Qs[idx];
    var sel    = (ans[q.id] || []).slice();
    var isLast = idx === Qs.length - 1;
    var pct    = Math.round(((idx + 1) / Qs.length) * 100);
    var maxR   = q.maxSel > 1 && sel.length >= q.maxSel;

    var html = '<div class="br-quiz br-fade-in">';
    html += '<div class="br-progress"><div class="br-progress-fill" style="width:' + pct + '%"></div></div>';
    html += '<div class="br-quiz-layout">';

    html += '<div class="br-q-panel">' +
      '<img class="br-q-panel-img" src="' + esc(q.imgSrc) + '" alt="" loading="lazy">' +
      '<div class="br-q-panel-overlay"></div>' +
      '<div class="br-q-panel-content">' +
        '<span class="br-q-panel-step">' + esc(q.step) + '</span>' +
        '<span class="br-q-panel-label">' + esc(q.imgLabel) + '</span>' +
      '</div>' +
    '</div>';

    html += '<div class="br-q-body">';
    html += '<div class="br-dots">';
    for (var d = 0; d < Qs.length; d++) {
      html += '<div class="br-dot' + (d === idx ? ' is-active' : d < idx ? ' is-done' : '') + '"></div>';
    }
    html += '</div>';

    html += '<h2 class="br-q-title">' + esc(q.q) + '</h2>';
    if (q.sub) html += '<p class="br-q-sub">' + esc(q.sub) + '</p>';

    var disabledKeys = [];
    var disabledNote = '';

    if (q.id === 'verlegeart' && (ans.eigenschaften || []).indexOf('trittschall') >= 0) {
      disabledKeys.push('kleben');
      disabledNote = 'Hinweis: „Verklebt" ist deaktiviert, weil verklebte Böden keine '
                   + 'integrierte Trittschalldämmung enthalten. Wähle „Klick-System", '
                   + 'um deine zuvor gewählte Eigenschaft beizubehalten.';
      if (sel.indexOf('kleben') >= 0) {
        sel = sel.filter(function (k) { return k !== 'kleben'; });
        ans.verlegeart = sel;
      }
    }

    if (q.type === 'img') {
      html += renderImgGrid(q, idx, sel);
    } else if (q.type === 'icon') {
      html += renderIconGrid(q, idx, sel, maxR);
    } else {
      html += renderList(q, idx, sel, disabledKeys);
    }
    if (disabledNote) {
      html += '<div class="br-tip" style="margin-top:14px">' + esc(disabledNote) + '</div>';
    }

    html += '<div id="br-tip-box"></div>';
    html += '</div>';
    html += '</div>';

    html += '<div class="br-nav">' +
      '<button class="br-nav-back" id="br-nav-back"' + (idx === 0 ? ' disabled' : '') + '>← Zurück</button>' +
      '<span class="br-nav-step">' + esc(q.step) + '</span>' +
      '<button class="br-nav-next' + (sel.length === 0 ? ' is-muted' : '') + '" id="br-nav-next">' +
        (isLast ? 'Ergebnisse →' : 'Weiter →') +
      '</button>' +
    '</div>';

    html += '</div>';

    root.innerHTML = html;
    bindOptions(q, idx);
    refreshTip(q, sel);

    document.getElementById('br-nav-back').addEventListener('click', function () {
      if (idx > 0) renderQuestion(idx - 1);
    });

    document.getElementById('br-nav-next').addEventListener('click', function () {
      var current = ans[q.id] || [];
      if (current.length === 0) {
        var tb = document.getElementById('br-tip-box');
        if (tb) tb.innerHTML = '<div class="br-tip br-tip--warn">Bitte wähle eine Option aus, um fortzufahren.</div>';
        return;
      }
      if (idx < Qs.length - 1) renderQuestion(idx + 1);
      else computeAndShow();
    });
  }

  /* ── OPTION RENDERERS ── */
  function renderImgGrid(q, idx, sel) {
    if (q.id !== 'raum') {
      var html = '<div class="br-img-grid">';
      q.opts.forEach(function (o) {
        var on = sel.indexOf(o.key) >= 0;
        html += '<div class="br-img-card' + (on ? ' is-on' : '') + '" data-key="' + o.key + '" data-qi="' + idx + '">' +
          '<div class="br-img-card-check">✓</div>' +
          '<img src="' + esc(o.img) + '" alt="' + esc(o.l) + '" loading="lazy">' +
          '<div class="br-img-card-body">' +
            '<div class="br-img-card-label">' + esc(o.l) + '</div>' +
            (o.s ? '<div class="br-img-card-sub">' + esc(o.s) + '</div>' : '') +
          '</div>' +
        '</div>';
      });
      html += '</div>';
      return html;
    }

    var html = '<div class="br-raum-groups">';
    q.opts.forEach(function (o) {
      var on = sel.indexOf(o.key) >= 0;
      html += '<div class="br-raum-block' + (on ? ' is-on' : '') + '" data-key="' + o.key + '" data-qi="' + idx + '">';
      html += '<div class="br-raum-block-check">✓</div>';
      html += '<div class="br-raum-block-img-wrap">' +
                '<img src="' + esc(o.img) + '" alt="' + esc(o.l) + '" loading="lazy">' +
              '</div>';
      html += '<div class="br-raum-block-body">';
      html += '<div class="br-raum-block-title">' + esc(o.l) + '</div>';
      if (o.rooms && o.rooms.length) {
        html += '<ul class="br-raum-block-list">';
        o.rooms.forEach(function (r) { html += '<li>' + esc(r) + '</li>'; });
        html += '</ul>';
      }
      html += '</div>';
      html += '</div>';
    });
    html += '</div>';
    return html;
  }

  function renderIconGrid(q, idx, sel, maxR) {
    var html = '<div class="br-icon-grid">';
    q.opts.forEach(function (o) {
      var on     = sel.indexOf(o.key) >= 0;
      var dimmed = maxR && !on;
      html += '<div class="br-icon-card' + (on ? ' is-on' : '') + (dimmed ? ' is-dim' : '') + '" data-key="' + o.key + '" data-qi="' + idx + '">' +
        '<div class="br-icon-card-check">✓</div>' +
        '<div class="br-icon-card-ic">' + o.ic + '</div>' +
        '<div class="br-icon-card-label">' + esc(o.l) + '</div>' +
        (o.s ? '<div class="br-icon-card-sub">' + esc(o.s) + '</div>' : '') +
      '</div>';
    });
    html += '</div>';
    return html;
  }

  function renderList(q, idx, sel, disabledKeys) {
    disabledKeys = disabledKeys || [];
    var html = '<div class="br-list">';
    q.opts.forEach(function (o) {
      var on  = sel.indexOf(o.key) >= 0;
      var off = disabledKeys.indexOf(o.key) >= 0;
      html += '<div class="br-list-item' + (on ? ' is-on' : '') + (off ? ' is-disabled' : '') + '" data-key="' + o.key + '" data-qi="' + idx + '"' + (off ? ' aria-disabled="true"' : '') + '>' +
        '<div class="br-list-radio' + (on ? ' is-on' : '') + '"></div>' +
        '<div class="br-list-ic">' + o.ic + '</div>' +
        '<div class="br-list-body">' +
          '<div class="br-list-label">' + esc(o.l) + '</div>' +
          (o.s ? '<div class="br-list-sub">' + esc(o.s) + '</div>' : '') +
        '</div>' +
      '</div>';
    });
    html += '</div>';
    return html;
  }

  /* ── OPTION CLICK BINDING ── */
  function bindOptions(q, idx) {
    root.querySelectorAll('[data-qi="' + idx + '"]').forEach(function (card) {
      card.addEventListener('click', function () {
        if (this.classList.contains('is-disabled')) return;
        var key = this.getAttribute('data-key');
        var sel = (ans[q.id] || []).slice();

        if (q.multi) {
          var pos = sel.indexOf(key);
          if (pos >= 0) {
            sel.splice(pos, 1);
          } else {
            if (sel.length < q.maxSel) {
              sel.push(key);
            } else {
              var tb = document.getElementById('br-tip-box');
              if (tb) tb.innerHTML = '<div class="br-tip br-tip--warn">Maximal ' + q.maxSel + ' Optionen wählbar — hebe eine Auswahl auf, um eine andere zu wählen.</div>';
              return;
            }
          }
        } else {
          sel = [key];
        }

        ans[q.id] = sel;

        /* Auto-set feuchtigkeit based on room group choice */
        if (q.id === 'raum' && sel.length > 0) {
          var chosenGroup = sel[0];
          if (chosenGroup === 'trocken_group') {
            ans.feuchtigkeit = ['trocken'];
          } else if (chosenGroup === 'feucht_group') {
            ans.feuchtigkeit = ['hoch'];
          } else if (chosenGroup === 'verkehr_group') {
            ans.feuchtigkeit = ['mittel'];
          }
        }

        var maxR = q.maxSel > 1 && sel.length >= q.maxSel;

        root.querySelectorAll('[data-qi="' + idx + '"]').forEach(function (c) {
          var k  = c.getAttribute('data-key');
          var on = sel.indexOf(k) >= 0;
          c.classList.toggle('is-on',  on);
          c.classList.toggle('is-dim', maxR && !on);
          var radio = c.querySelector('.br-list-radio');
          if (radio) radio.classList.toggle('is-on', on);
        });

        var nextBtn = document.getElementById('br-nav-next');
        if (nextBtn) nextBtn.classList.toggle('is-muted', sel.length === 0);

        if (!maxR) {
          var tb2 = document.getElementById('br-tip-box');
          if (tb2) {
            var warn = tb2.querySelector('.br-tip--warn');
            if (warn && warn.textContent.indexOf('Maximal') >= 0) tb2.innerHTML = '';
          }
        }

        refreshTip(q, sel);
      });
    });
  }

  /* ─── TIPS ─── */
  function refreshTip(q, sel) {
    var box = document.getElementById('br-tip-box');
    if (!box) return;
    var tips = [];
    (sel || []).forEach(function (key) {
      var o = findOpt(q, key);
      if (o && o.tip) tips.push(o.tip);
    });
    box.innerHTML = tips.length
      ? '<div class="br-tip">' + tips.map(esc).join('<br>') + '</div>'
      : '';
  }

  function findOpt(q, key) {
    for (var i = 0; i < q.opts.length; i++) {
      if (q.opts[i].key === key) return q.opts[i];
    }
    return null;
  }

  /* ─── SCORING ENGINE ─── */
  function scoreProduct(p, relax) {
    relax = relax || {};
    var score = 0;

    var raumRaw    = (ans.raum || [])[0] || '';
    var raumKey    = raumRaw === 'trocken_group' ? 'wohnzimmer'
                   : raumRaw === 'feucht_group'  ? 'bad'
                   : raumRaw === 'verkehr_group' ? 'gewerbe'
                   : raumRaw;
    var nutzKey    = (ans.nutzung       || [])[0] || '';
    var feuchtKey  = (ans.feuchtigkeit  || [])[0] || '';
    var eigKeys    =  ans.eigenschaften || [];
    var optikKeys  =  ans.optik         || [];
    var verlegeKey = (ans.verlegeart    || [])[0] || '';

    var staerke = parseStaerke(p);
    var nk      = p.nk;

    /* ── HARD EXCLUSIONS ── */
    if (raumKey === 'bad' && !isFeuchtraum(p)) return -1;
    if (feuchtKey === 'hoch' && !isFeuchtraum(p)) return -1;
    if (raumKey === 'flur'    && p.nk > 0 && p.nk < 32 && !relax.nk) return -1;
    if (raumKey === 'gewerbe' && p.nk > 0 && p.nk < 33 && !relax.nk) return -1;
    if ((raumKey === 'wohnzimmer' || raumKey === 'schlafzimmer' || raumKey === 'kueche') && p.nk >= 42) return -1;
    if (raumKey === 'kueche' && isParkett(p)) return -1;
    if (raumKey === 'kueche' && isLaminat(p) && !isFeuchtraum(p)) return -1;
    if (verlegeKey === 'klick'  && !isKlick(p)  && !relax.verlegeart) return -1;
    if (verlegeKey === 'kleben' && !isKleben(p) && !relax.verlegeart) return -1;
    if (eigKeys.indexOf('trittschall') >= 0 && !hasTrittschall(p) && !relax.trittschall) return -1;
    if (nutzKey === 'kinder'    && !hasTrittschall(p) && !relax.kinderTrittschall) return -1;
    if (nutzKey === 'haustiere' && nsTier(p) < 3      && !relax.haustiereKratz)   return -1;

    /* ── FEUCHTIGKEIT (25%) ── */
    var fSc = 0;
    if (feuchtKey === 'hoch') {
      fSc = 60;
    } else if (feuchtKey === 'trocken') {
      if (isParkett(p))                             fSc = 50;
      else if (isLaminat(p))                        fSc = 42;
      else if (isVinyl(p) || isDesignboden(p))      fSc = 28;
    } else if (feuchtKey === 'mittel') {
      var wetProne = (raumKey === 'kueche' || raumKey === 'bad');
      if (wetProne) {
        if (isFeuchtraum(p))                        fSc = 50;
        else if (isVinyl(p) || isDesignboden(p))    fSc = 32;
        else if (isLaminat(p))                      fSc = 18;
        else if (isParkett(p))                      fSc = -10;
      } else {
        if (isParkett(p))                           fSc = 45;
        else if (isLaminat(p))                      fSc = 38;
        else if (isVinyl(p) || isDesignboden(p))    fSc = 30;
        if (isFeuchtraum(p))                        fSc += 15;
      }
    }
    score += fSc * 0.25;

    /* ── NUTZUNG (20%) ── */
    var nSc  = 0;
    var tier = nsTier(p);
    if (nutzKey === 'haustiere') {
      if (isParkett(p))                             nSc -= 15;
      if (isVinyl(p) || isDesignboden(p))           nSc += 20;
      else if (isLaminat(p))                        nSc += 8;
      if      (tier === 4) nSc += 40;
      else if (tier === 3) nSc += 32;
      else if (tier >= 1)  nSc += 8;
      else                 nSc -= 20;
      if (staerke >= 6) nSc += 8;
    } else if (nutzKey === 'kinder') {
      if (isVinyl(p) || isDesignboden(p))           nSc += 20;
      else if (isLaminat(p))                        nSc += 16;
      if (hasTrittschall(p))                        nSc += 25;
      else                                          nSc -= 12;
      if      (tier === 4) nSc += 20;
      else if (tier === 3) nSc += 15;
      else if (tier >= 1)  nSc += 5;
      else                 nSc -= 15;
      if (staerke >= 5)    nSc += 8;
    } else if (nutzKey === 'stark') {
      if      (nk >= 43)   nSc += 60;
      else if (nk >= 42)   nSc += 55;
      else if (nk >= 41)   nSc += 45;
      else if (nk >= 33)   nSc += 35;
      else if (nk >= 32)   nSc += 18;
      else if (nk === 31)  nSc += 8;
      if (staerke >= 8)    nSc += 18;
      else if (staerke >= 6) nSc += 10;
      if (isVinyl(p) || isDesignboden(p)) nSc += 10;
    } else {
      if (isParkett(p))                            nSc += 32;
      else if (isLaminat(p))                       nSc += 26;
      else if (isVinyl(p) || isDesignboden(p))     nSc += 20;
      if (nk >= 23 && nk <= 33)                    nSc += 12;
    }
    score += nSc * 0.20;

    /* ── EIGENSCHAFTEN (20%) ── */
    var eSc = 0;
    if (eigKeys.indexOf('fbh_eig') >= 0) {
      if (isFBH(p)) eSc += 50; else eSc -= 15;
    }
    if (eigKeys.indexOf('trittschall') >= 0) {
      if (hasTrittschall(p)) eSc += 50; else eSc -= 30;
    }
    if (eigKeys.indexOf('kratzfest') >= 0) {
      if      (tier === 4) eSc += 45;
      else if (tier === 3) eSc += 35;
      else if (tier === 2) eSc += 22;
      else if (tier === 1) eSc += 10;
      if (isVinyl(p) || isDesignboden(p)) eSc += 12;
    }
    if (eigKeys.indexOf('pflegeleicht') >= 0) {
      if (isVinyl(p) || isDesignboden(p))  eSc += 45;
      else if (isLaminat(p))               eSc += 20;
      else if (isParkett(p))               eSc += 5;
    }
    if (eigKeys.indexOf('natuerlich') >= 0) {
      var isGreenline = p.title.toLowerCase().indexOf('greenline') >= 0;
      if (isGreenline || isParkett(p))  eSc += 35;
      else if (isLaminat(p))            eSc += 10;
      else                              eSc -= 25;
    }
    score += eSc * 0.20;

    /* ── RAUM (15%) ── */
    var rSc = 0;
    if (raumKey === 'bad') {
      rSc = 50;
      if (isFliese(p)) rSc += 50;
    } else if (raumKey === 'kueche') {
      if (isFeuchtraum(p))                          rSc += 50;
      else if (isVinyl(p) || isDesignboden(p))      rSc += 28;
      else if (isLaminat(p))                        rSc += 10;
      else if (isParkett(p))                        rSc -= 20;
      if (isFliese(p))                              rSc += 30;
    } else if (raumKey === 'flur') {
      if      (nk >= 43)  rSc += 73;
      else if (nk >= 42)  rSc += 65;
      else if (nk >= 41)  rSc += 55;
      else if (nk >= 33)  rSc += 50;
      else if (nk >= 32)  rSc += 42;
      else if (nk === 0)  rSc -= 30;
      if (isFeuchtraum(p)) rSc += 12;
      if (isFliese(p))     rSc += 20;
    } else if (raumKey === 'gewerbe') {
      if      (nk >= 43)  rSc += 82;
      else if (nk >= 42)  rSc += 74;
      else if (nk >= 41)  rSc += 66;
      else if (nk >= 33)  rSc += 50;
      else if (nk === 0)  rSc -= 30;
      if (isFliese(p))    rSc += 15;
    } else if (raumKey === 'wohnzimmer') {
      if (isParkett(p))                             rSc += 40;
      else if (isLaminat(p))                        rSc += 30;
      else if (isVinyl(p) || isDesignboden(p))      rSc += 20;
      if (nk >= 23 && nk <= 33)                     rSc += 12;
      if (isFischgraet(p))                          rSc += 8;
      if (isFliese(p) && optikKeys.indexOf('fliese') < 0) rSc -= 50;
    } else if (raumKey === 'schlafzimmer') {
      if (isParkett(p))                             rSc += 40;
      else if (isLaminat(p))                        rSc += 30;
      else if (isVinyl(p) || isDesignboden(p))      rSc += 20;
      if (hasTrittschall(p))                        rSc += 25;
      if (nk >= 23 && nk <= 33)                     rSc += 8;
      if (isFischgraet(p))                          rSc += 6;
      if (isFliese(p) && optikKeys.indexOf('fliese') < 0) rSc -= 70;
    }
    score += rSc * 0.15;

    /* ── OPTIK (10%) ── */
    var oSc = 0;
    var hasSpecific = optikKeys.some(function (k) { return k !== 'egal'; });
    if (!hasSpecific) {
      oSc = 15;
    } else {
      optikKeys.forEach(function (key) {
        if (key !== 'egal' && matchesOptikKey(p, key)) oSc += 50;
      });
      if (oSc === 0) oSc = -25;
    }
    score += oSc * 0.10;

    /* ── BONUS (10%) ── */
    var bSc = 0;
    if (isFeuchtraum(p) && (raumKey === 'bad' || feuchtKey === 'hoch')) bSc += 25;
    score += bSc * 0.10;

    return Math.max(0, Math.round(score));
  }

  function scoreAll(relax) {
    return products
      .map(function (p) { return { p: p, score: scoreProduct(p, relax) }; })
      .filter(function (x) { return x.score >= 0; })
      .sort(function (a, b) { return b.score - a.score; });
  }

  /* ─── COMPUTE + SHOW RESULTS ─── */
  function computeAndShow() {
    root.innerHTML =
      '<div class="br-loading br-fade-in">' +
        '<div class="br-spinner"></div>' +
        '<p>Wir berechnen deine persönlichen Empfehlungen …</p>' +
      '</div>';

    setTimeout(function () {
      var raumRaw    = (ans.raum         || [])[0] || '';
      var raumKey    = raumRaw === 'trocken_group' ? 'wohnzimmer'
                     : raumRaw === 'feucht_group'  ? 'bad'
                     : raumRaw === 'verkehr_group' ? 'gewerbe'
                     : raumRaw;
      var nutzKey    = (ans.nutzung      || [])[0] || '';
      var feuchtKey  = (ans.feuchtigkeit || [])[0] || '';
      var eigKeys    = ans.eigenschaften || [];
      var optikKeys  = ans.optik || [];
      var verlegeKey = (ans.verlegeart   || [])[0] || '';
      var specific   = optikKeys.filter(function (k) { return k !== 'egal'; });

      var scored = scoreAll();

      var relaxedSummary    = null;
      var hasVerlegePref    = !!(verlegeKey && verlegeKey !== 'egal_verl');
      var hasTrittschallEig = eigKeys.indexOf('trittschall') >= 0;
      var isNkRoom          = (raumKey === 'flur' || raumKey === 'gewerbe');

      if (scored.length === 0 && isNkRoom) {
        var attempt1 = scoreAll({ nk: true });
        if (attempt1.length > 0) { scored = attempt1; relaxedSummary = 'nk'; }
      }
      if (scored.length === 0 && nutzKey === 'kinder') {
        var attempt2a = scoreAll({ nk: isNkRoom, kinderTrittschall: true });
        if (attempt2a.length > 0) { scored = attempt2a; relaxedSummary = 'kinder_trittschall'; }
      }
      if (scored.length === 0 && nutzKey === 'haustiere') {
        var attempt2b = scoreAll({ nk: isNkRoom, haustiereKratz: true });
        if (attempt2b.length > 0) { scored = attempt2b; relaxedSummary = 'haustiere_kratz'; }
      }
      if (scored.length === 0 && hasTrittschallEig) {
        var attempt3 = scoreAll({
          nk: isNkRoom,
          kinderTrittschall: nutzKey === 'kinder',
          haustiereKratz: nutzKey === 'haustiere',
          trittschall: true,
        });
        if (attempt3.length > 0) { scored = attempt3; relaxedSummary = 'trittschall'; }
      }
      if (scored.length === 0 && hasVerlegePref) {
        var attempt4 = scoreAll({
          nk: isNkRoom,
          kinderTrittschall: nutzKey === 'kinder',
          haustiereKratz: nutzKey === 'haustiere',
          trittschall: hasTrittschallEig,
          verlegeart: true,
        });
        if (attempt4.length > 0) { scored = attempt4; relaxedSummary = 'verlegeart'; }
      }

      var softWarning = null;
      if (relaxedSummary === 'verlegeart') {
        softWarning = 'Für deine gewählte Verlegeart fanden wir keine passenden Produkte — wir zeigen dir die besten Alternativen mit anderen Verlegearten.';
      } else if (relaxedSummary === 'nk') {
        softWarning = 'Für die geforderte Nutzungsklasse fanden wir keine passenden Produkte — wir zeigen dir die besten Alternativen aus dem Sortiment.';
      } else if (relaxedSummary === 'trittschall') {
        softWarning = 'Für die geforderte integrierte Trittschalldämmung fanden wir keine passenden Produkte — wir zeigen dir die besten Alternativen.';
      } else if (relaxedSummary === 'kinder_trittschall') {
        softWarning = 'Für die Anforderung an integrierte Trittschalldämmung (Kinder) fanden wir keine perfekt passenden Produkte — wir zeigen dir die besten verfügbaren Alternativen.';
      } else if (relaxedSummary === 'haustiere_kratz') {
        softWarning = 'Für eine besonders kratzresistente Oberfläche (Haustiere) fanden wir keine perfekt passenden Produkte — wir zeigen dir die besten verfügbaren Alternativen.';
      }
      if (eigKeys.indexOf('natuerlich') >= 0 && (raumKey === 'bad' || feuchtKey === 'hoch')) {
        softWarning = 'Echte Naturmaterialien wie Parkett sind in Feuchträumen nicht möglich — wir zeigen dir feuchtraumgeeignete Böden, die deinem Wunsch nach Natürlichkeit am nächsten kommen (z. B. Greenline).';
      }
      if (nutzKey === 'haustiere' && optikKeys.indexOf('fischgraet') >= 0 && verlegeKey === 'kleben') {
        softWarning = 'Die Kombination Haustiere + Fischgrät + Klebe-Verlegung ist in der Praxis schwierig: Fischgrät ist meist Echtholz-Parkett mit Klick-System und für Haustier-Haushalte weniger geeignet. Wir empfehlen, den Bodenberater neu zu starten und stattdessen Vinyl oder Designboden zu wählen — kratzresistent und auch zum Kleben verfügbar.';
      }

      var COLOR_KEYS  = ['hell', 'mittel', 'dunkel'];
      var colorChosen = specific.filter(function (k) { return COLOR_KEYS.indexOf(k) >= 0; });
      var patternKeys = specific.filter(function (k) { return COLOR_KEYS.indexOf(k) < 0; });

      function optikTier(p) {
        if (specific.length === 0) return 0;
        var matchColor   = colorChosen.length === 0 || colorChosen.some(function (k) { return matchesOptikKey(p, k); });
        var matchPattern = patternKeys.every(function (k) { return matchesOptikKey(p, k); });
        if (matchColor && matchPattern) return 2;
        if (matchesAnyOptik(p, specific)) return 1;
        return 0;
      }

      var optikFiltered = scored;
      if (specific.length > 0) {
        var strictMatch = scored.filter(function (x) { return optikTier(x.p) >= 1; });
        if (strictMatch.length >= MIN_R) {
          optikFiltered = strictMatch;
        } else if (strictMatch.length > 0) {
          optikFiltered = strictMatch;
          if (!softWarning) {
            softWarning = 'Für deine gewählte Optik haben wir ' + strictMatch.length +
              ' passende Produkt' + (strictMatch.length === 1 ? '' : 'e') + ' gefunden. Darunter zeigen wir dir weitere technisch passende Alternativen.';
          }
          var padding = scored.filter(function (x) { return optikTier(x.p) === 0; });
          optikFiltered = strictMatch.concat(padding).slice(0, MAX_R);
        } else {
          if (!softWarning) {
            softWarning = 'Für deine gewählte Optik konnten wir leider keine exakten Treffer finden. Wir zeigen dir die technisch besten Alternativen aus dem Sortiment.';
          }
        }
      }

      optikFiltered.sort(function (a, b) {
        var ta = optikTier(a.p), tb = optikTier(b.p);
        if (ta !== tb) return tb - ta;
        return b.score - a.score;
      });

      var shown = optikFiltered.slice(0, MAX_R);

      shown.forEach(function (item, i) {
        var eligible = (specific.length === 0) || (optikTier(item.p) >= 1);
        item.isFullMatch = i < OPTIK_SLOTS && eligible;
      });

      if (!softWarning && verlegeKey === 'kleben' && optikKeys.indexOf('fischgraet') >= 0 &&
          shown.some(function (it) { return it.p.title.toLowerCase().indexOf('klick') >= 0 && isKleben(it.p); })) {
        softWarning = 'Hinweis: Einige Fischgrät-Produkte tragen „Klick" im Namen, sind laut Hersteller aber auch zum Verkleben geeignet und passen daher zu deiner gewählten Verlegeart.';
      }

      window.__brLastResults = shown.slice(0, 6).map(function (item) {
        return {
          title: item.p.title,
          url:   'https://www.reud-bodenarena.de' + item.p.url,
          img:   item.p.img,
          art:   item.p.art,
        };
      });

      /* ── GA4: bodenberater_completed — fired here, after scoring succeeds,
         using the already-mapped raumKey for readable values in GA4 ── */
      if (window.gtag) gtag('event', 'bodenberater_completed', {
        raum:       raumKey,    /* 'wohnzimmer' | 'bad' | 'gewerbe' */
        nutzung:    nutzKey,    /* 'normal' | 'kinder' | 'haustiere' | 'stark' */
        verlegeart: verlegeKey, /* 'klick' | 'kleben' | 'egal_verl' */
      });

      renderResults(shown, softWarning);
    }, 400);
  }

  /* ─── RENDER RESULTS ─── */
  function renderResults(items, softWarning) {
    var html = '<div class="br-results br-fade-in">';

    html += '<div class="br-results-head">' +
      '<span class="br-eyebrow">Deine Empfehlung</span>' +
      '<h2 class="br-results-title">Persönlich ausgewählt für dich</h2>' +
      '<p class="br-results-sub">Basierend auf deinen Angaben haben wir die passendsten Böden für dich zusammengestellt.</p>' +
    '</div>';

    if (softWarning) {
      html += '<div class="br-approx-note">' + esc(softWarning) + '</div>';
    }

    if (items.length === 0) {
      html += '<div class="br-empty">' +
        '<p>Keine passenden Produkte gefunden.</p>' +
        '<p class="br-empty-hint">Tipp: Wähle bei der Optik „Keine Präferenz" für eine größere Auswahl, oder starte den Bodenberater neu mit anderen Kriterien.</p>' +
        '<button class="br-btn-restart" id="br-restart-empty">↺ Bodenberater neu starten</button>' +
      '</div>';
    } else {
      if (items.some(function (x) { return x.score <= 5; })) {
        html += '<div class="br-approx-note">Einige Ergebnisse sind ungefähre Übereinstimmungen. Unsere Berater helfen dir gerne weiter — kostenlos und unverbindlich.</div>';
      }

      html += '<div class="br-results-grid">';
      items.forEach(function (item) {
        var p          = item.p;
        var isBestPick = !!item.isFullMatch;
        var tags       = buildTags(p);

        html += '<div class="br-result-card' + (isBestPick ? ' is-top' : '') + '">';
        if (isBestPick) {
          html += '<div class="br-result-badge">Beste Empfehlung</div>';
        }
        if (p.img) {
          html += '<div class="br-result-img-wrap"><img class="br-result-img" src="' + esc(p.img) + '" alt="' + esc(p.title) + '" loading="lazy"></div>';
        } else {
          html += '<div class="br-result-img-ph">🪵</div>';
        }
        html += '<div class="br-result-body">';
        if (p.art) html += '<div class="br-result-type">' + esc(p.art) + '</div>';
        html += '<div class="br-result-name">' + esc(p.title) + '</div>';
        if (tags.length) {
          html += '<div class="br-result-tags">';
          tags.forEach(function (t) { html += '<span class="br-result-tag">' + esc(t) + '</span>'; });
          html += '</div>';
        }
        /* ── GA4 product click: data-attribute approach — safe with any product title ── */
        html += '<a class="br-result-cta br-track-click"' +
          ' data-product-title="' + esc(p.title) + '"' +
          ' href="' + esc(p.url) + '"' +
          ' target="_blank" rel="noopener noreferrer">Zum Produkt →</a>';
        html += '</div></div>';
      });
      html += '</div>';
    }

    html += buildResultsCta();
    html += buildEmailCapture();
    html += '<div class="br-results-foot"><button class="br-btn-restart" id="br-restart-foot">↺ Bodenberater neu starten</button></div>';
    html += '</div>';

    root.innerHTML = html;

    /* ── GA4: product click listeners — attached after innerHTML, no inline onclick ── */
    root.querySelectorAll('.br-track-click').forEach(function (a) {
      a.addEventListener('click', function () {
        if (window.gtag) gtag('event', 'bodenberater_product_click', {
          product_title: this.getAttribute('data-product-title'),
        });
      });
    });

    /* Restart buttons */
    root.querySelectorAll('#br-restart-empty, #br-restart-foot').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (window.gtag) gtag('event', 'bodenberater_restarted');
        ans = {};
        renderEntry();
      });
    });

    bindEmailCapture();
  }

  /* ─── PRODUCT TAGS ─── */
  function buildTags(p) {
    var tags = [];
    if (isFeuchtraum(p))    tags.push('Feuchtraumgeeignet');
    if (isFBH(p))           tags.push('FBH-geeignet');
    if (hasTrittschall(p))  tags.push('Trittschalldämmung');
    if (isKlick(p))         tags.push('Klick-System');
    else if (isKleben(p))   tags.push('Klebe-System');
    if (nsTier(p) === 4)    tags.push('NS ' + p.nutzungsschicht);
    if (p.nk >= 31)         tags.push('NK ' + p.nk);
    return tags.slice(0, 4);
  }

  /* ── COLLECTIONS CTA ── */
  function buildResultsCta() {
    return '' +
      '<div class="br-results-cta">' +
        '<span class="br-eyebrow">Unsere Kollektionen</span>' +
        '<h3 class="br-results-cta-headline">Noch mehr Inspiration entdecken</h3>' +
        '<a class="br-results-cta-btn" href="/collections/alle-bodenbelage/" target="_blank" rel="noopener noreferrer">' +
          'Alle Bodenbeläge ansehen <span class="br-results-cta-arrow">→</span>' +
        '</a>' +
      '</div>';
  }

  /* ── EMAIL CAPTURE ── */
  function buildEmailCapture() {
    return '' +
      '<div class="br-email">' +
        '<div class="br-email-inner">' +
          '<div class="br-email-text">' +
            '<h3>Angebote &amp; Neuigkeiten</h3>' +
            '<p>Trag dich ein und erhalte exklusive Angebote und Neuheiten direkt in dein Postfach.</p>' +
          '</div>' +
          '<div id="br-ec-form">' +
            '<div class="br-email-fields">' +
              '<input class="br-input" type="text"  id="br-ec-fname" placeholder="Vorname *"  autocomplete="given-name">' +
              '<input class="br-input" type="text"  id="br-ec-lname" placeholder="Nachname *" autocomplete="family-name">' +
              '<input class="br-input" type="email" id="br-ec-email" placeholder="E-Mail *"   autocomplete="email">' +
            '</div>' +
            '<label class="br-consent-row">' +
              '<input type="checkbox" id="br-ec-consent" class="br-consent-cb">' +
              '<span class="br-consent">Ja, ich möchte den Newsletter mit Angeboten und Neuigkeiten von REUD Bodenarena per E-Mail erhalten. ' +
                'Die Einwilligung kann ich jederzeit widerrufen. Es gilt die ' +
                '<a href="/policies/privacy-policy" target="_blank" rel="noopener">Datenschutzerklärung</a>.' +
              '</span>' +
            '</label>' +
            '<div class="br-email-err" id="br-ec-err" role="alert" style="display:none"></div>' +
            '<div id="br-ec-fb"></div>' +
            '<button type="button" class="br-nav-next" id="br-ec-btn" style="width:100%;justify-content:center;margin-top:12px;" disabled>Anmelden →</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function bindEmailCapture() {
    var KLAVIYO_LIST_ID    = 'XpFqFR';
    var KLAVIYO_COMPANY_ID = 'SBDBDr';

    var btn     = document.getElementById('br-ec-btn');
    var consent = document.getElementById('br-ec-consent');
    var err     = document.getElementById('br-ec-err');
    var fb      = document.getElementById('br-ec-fb');
    var fname   = document.getElementById('br-ec-fname');
    var lname   = document.getElementById('br-ec-lname');
    var email   = document.getElementById('br-ec-email');

    if (!btn) return;

    consent.addEventListener('change', function () {
      btn.disabled = !consent.checked;
    });

    function showError(msg) {
      err.textContent   = msg;
      err.style.display = 'block';
    }
    function clearError() {
      err.textContent   = '';
      err.style.display = 'none';
    }

    btn.addEventListener('click', function () {
      clearError();
      fb.innerHTML = '';

      var fnameVal = fname.value.trim();
      var lnameVal = lname.value.trim();
      var emailVal = email.value.trim();

      if (!fnameVal || !lnameVal) {
        showError('Bitte gib Vorname und Nachname an.');
        return;
      }
      if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        showError('Bitte gib eine gültige E-Mail-Adresse ein.');
        return;
      }
      if (!consent.checked) {
        showError('Bitte bestätige die Einwilligung zum Empfang des Newsletters.');
        return;
      }

      btn.disabled    = true;
      btn.textContent = 'Wird gesendet …';

      var quizSummary = buildAnswerNote();

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2023-12-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Bodenberater',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email:      emailVal,
                    first_name: fnameVal,
                    last_name:  lnameVal,
                    properties: {
                      bodenberater_antworten:    quizSummary,
                      bodenberater_datum:        new Date().toLocaleDateString('de-DE'),
                      consent_bodenberater:      true,
                      bodenberater_empfehlung_1: window.__brLastResults[0] ? window.__brLastResults[0].title + ' — ' + window.__brLastResults[0].url : '',
                      bodenberater_empfehlung_2: window.__brLastResults[1] ? window.__brLastResults[1].title + ' — ' + window.__brLastResults[1].url : '',
                      bodenberater_empfehlung_3: window.__brLastResults[2] ? window.__brLastResults[2].title + ' — ' + window.__brLastResults[2].url : '',
                      bodenberater_empfehlung_4: window.__brLastResults[3] ? window.__brLastResults[3].title + ' — ' + window.__brLastResults[3].url : '',
                      bodenberater_empfehlung_5: window.__brLastResults[4] ? window.__brLastResults[4].title + ' — ' + window.__brLastResults[4].url : '',
                      bodenberater_empfehlung_6: window.__brLastResults[5] ? window.__brLastResults[5].title + ' — ' + window.__brLastResults[5].url : '',
                    }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST_ID } } }
          }
        })
      })
      .then(function (res) {
        if (res.status === 202 || res.ok) {
          fb.innerHTML = '<div class="br-email-ok">✓ Vielen Dank! Du erhältst in Kürze eine Bestätigungs-E-Mail.</div>';
          document.querySelector('.br-email-fields').style.display = 'none';
          document.querySelector('.br-consent-row').style.display  = 'none';
          btn.style.display = 'none';
        } else {
          return res.json().then(function (data) { throw new Error(JSON.stringify(data)); });
        }
      })
      .catch(function (e) {
        console.warn('[Bodenberater] Klaviyo error', e);
        showError('Anmeldung fehlgeschlagen. Bitte versuche es später erneut.');
        btn.disabled    = false;
        btn.textContent = 'Anmelden →';
      });
    });
  }

  /* ─── ANSWER SUMMARY for Klaviyo ─── */
  function buildAnswerNote() {
    var labels = {
      raum:          'Raum',
      nutzung:       'Nutzung',
      eigenschaften: 'Eigenschaften',
      optik:         'Optik',
      verlegeart:    'Verlegeart',
    };
    return Qs.map(function (q) {
      var sel = ans[q.id] || [];
      if (!sel.length) return null;
      var ls = sel.map(function (key) {
        var o = findOpt(q, key);
        return o ? o.l : key;
      });
      return (labels[q.id] || q.id) + ': ' + ls.join(', ');
    }).filter(Boolean).join('\n');
  }

  /* ─── ESCAPE HTML ─── */
  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

} /* BodenberaterInit */